/**
 * @fileoverview The guided-tour script for the document builder's Section-editor mode.
 * @module ui-admin/help/sectionsTour
 *
 * Responsibilities: - Define the ordered list of `TourStep`s that walk a first-time user through
 *                     the Section-editor (the default builder mode), each targeting a real on-page
 *                     element via a `[data-tour-sections="…"]` selector so the spotlight lands on
 *                     the live UI.
 * Dependencies:     - `TourStep` from the help types.
 * Architecture:     - Pure static data bundled with the app (no API/Dexie, no mock data). Shared by
 *                     tenant-web and system-web; both pages carry matching `data-tour-sections`
 *                     attributes. A dedicated attribute namespace (`data-tour-sections`) keeps these
 *                     anchors distinct from the canvas tour's `data-tour` anchors on the same page.
 * Notes:            - GuidedTour centres the tooltip when a `targetSelector` is empty or missing, so
 *                     the tour still runs gracefully if a host page omits an optional target.
 */

import type { TourStep } from './types';

/** Stable `data-tour-sections` attribute values used as the Section-editor tour's selector targets. */
export const SECTIONS_TOUR_TARGET = {
  modeToggle: 'mode-toggle',
  sectionList: 'section-list',
  sectionToggle: 'section-toggle',
  sectionHelp: 'section-help',
  sectionConfig: 'section-config',
  pageSetup: 'page-setup',
  recreate: 'recreate',
  livePreview: 'live-preview',
  preview: 'preview',
  save: 'save',
  help: 'help',
} as const;

const sel = (target: string) => `[data-tour-sections="${target}"]`;

/**
 * @contract SECTIONS_TOUR_STEPS
 * Purpose: Ordered walkthrough of the document builder's Section-editor mode.
 * Rules: Step order is the tour order; every non-welcome step targets a real element selector.
 */
export const SECTIONS_TOUR_STEPS: TourStep[] = [
  {
    targetSelector: '',
    title: 'Welcome to the Document Builder',
    body: 'This is the Section editor \u2014 the quickest way to assemble a document. You toggle the sections you want, configure each one, and watch the live preview update. Let\u2019s take a quick look.',
    placement: 'center',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.modeToggle),
    title: 'Sections vs. Canvas',
    body: 'Switch between the Section editor (this guided, form-based mode) and the free-form Canvas builder. Both produce the same document \u2014 use whichever fits the job.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.sectionList),
    title: 'Your document sections',
    body: 'Every part of the document \u2014 header, recipient block, violations, photos, signatures, and more \u2014 is listed here in the order it prints.',
    placement: 'right',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.sectionToggle),
    title: 'Turn a section on or off',
    body: 'Click the green check / grey ✕ to include or exclude a section. Disabled sections are skipped when the document is generated.',
    placement: 'right',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.sectionHelp),
    title: 'Per-section help',
    body: 'Click the ? on any section for a quick explanation of what it is, how it renders in the PDF, and configuration tips \u2014 with a link to the full help.',
    placement: 'right',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.sectionConfig),
    title: 'Configure the selected section',
    body: 'Selecting a section opens its settings here. Choose fields, layout, and wording \u2014 type {{ to insert merge fields that fill in per inspection.',
    placement: 'left',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.pageSetup),
    title: 'Page setup',
    body: 'Open Page Setup to choose paper size, orientation, margins, and an optional page border.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.recreate),
    title: 'Start from an existing document',
    body: 'Already have a PDF or Word document? Recreate it into an editable template with AI, then fine-tune the sections here.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.livePreview),
    title: 'Live preview',
    body: 'The preview shows exactly what your PDF will look like \u2014 the same engine renders both \u2014 and updates as you change sections.',
    placement: 'left',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.preview),
    title: 'Full-screen preview',
    body: 'Open a larger, full-screen preview to review the whole document before saving.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.save),
    title: 'Save & publish',
    body: 'Save keeps your draft. Publishing makes the template available for generating real documents.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(SECTIONS_TOUR_TARGET.help),
    title: 'Help is always here',
    body: 'Reopen this tour or the illustrated help any time from the Help and Tour buttons. Happy building!',
    placement: 'bottom',
  },
];
